import { CustomHeaderTooltipComponent } from '@components/shared/grids/custom-header-tooltip/custom-header-tooltip.component';
import { formatCurrencyUSD, formatNumberDisplay, formatPercentageDisplay } from '@shared/grids/grid-formatters.config';
import { CellClassParams, ColDef, ICellEditorParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { ValuesGridRow } from './opportunities-values-grid.component';

export interface ValidationContext {
    isCreateEstimate: boolean;
}

interface CellValidationResult {
    isValid: boolean;
    message?: string;
}

interface CellValidationParams {
    data: ValuesGridRow;
    value: any;
    node: any;
    colDef?: ColDef | null;
    column?: any;
    api?: any;
}

function getPotentialCellValue(data: ValuesGridRow | undefined): number | null {
    if (!data) {
        return null;
    }

    if (data.fieldType === 'percentage' && data.potentialValue != null) {
        return data.potentialValue * 100;
    }

    return data.potentialValue ?? null;
}

function formatPotentialCellDisplay(params: ValueFormatterParams): string {
    const data = params.data as ValuesGridRow;
    const value = params.value as number | null | undefined;

    if (data?.fieldType === 'currency') {
        return formatCurrencyUSD(value);
    }

    if (data?.fieldType === 'percentage') {
        return value != null ? `${value.toFixed(2)}%` : '';
    }

    if (data?.fieldType === 'number') {
        return formatNumberDisplay(value);
    }

    return '';
}

function setPotentialCellValue(params: { data: ValuesGridRow; newValue: any }): boolean {
    const data = params.data as ValuesGridRow;

    if (params.newValue === '' || params.newValue == null) {
        data.potentialValue = undefined;
        return true;
    }

    const parsed = parseFloat(params.newValue as string);
    if (isNaN(parsed)) {
        return false;
    }

    if (data.fieldType === 'percentage') {
        data.potentialValue = parsed / 100;
    } else {
        data.potentialValue = parsed;
    }

    return true;
}

export function validateCellValue(
    params: CellValidationParams,
    validationContext?: ValidationContext
): CellValidationResult {
    const data = params.data;
    const value = params.value;
    const fieldKey = data?.fieldKey;

    if (!data?.isEditable) {
        return { isValid: true };
    }

    if (fieldKey === 'potentialValue') {
        if (validationContext?.isCreateEstimate && (value === null || value === undefined || value === '')) {
            return {
                isValid: false,
                message: 'Potential Value is required when creating an estimate.'
            };
        }

        if (value != null && typeof value === 'number') {
            const maxValue = 99999999999999999n;
            if (value > maxValue) {
                return {
                    isValid: false,
                    message: `*Input exceeds the maximum value of ${maxValue.toLocaleString()}`
                };
            }
        }
    }

    if (fieldKey === 'proposedGrossMarginPercentage') {
        if (value != null && typeof value === 'number') {
            const data = params.data;
            const fieldType = data?.fieldType;
            if (fieldType === 'percentage') {
                if (value < 0) {
                    return {
                        isValid: false,
                        message: 'Percentage must be between 0 and 9999%'
                    };
                }
                if (value > 9999) {
                    return {
                        isValid: false,
                        message: 'Percentage must be between 0 and 9999%'
                    };
                }
            }
        }
    }

    if (fieldKey === 'potentialTermAverage') {
        if (value != null && typeof value === 'number') {
            if (value < 0) {
                return {
                    isValid: false,
                    message: 'Term Average must be a positive currency amount.'
                };
            }
        }
    }

    if (fieldKey === 'potentialNumberOfTerms') {
        if (value != null && typeof value === 'number') {
            if (!Number.isInteger(value) || value < 0) {
                return {
                    isValid: false,
                    message: '# of Terms must be a positive integer.'
                };
            }
            if (value > 99) {
                return {
                    isValid: false,
                    message: '# of Terms must be less than 99.'
                };
            }
        }
    }

    return { isValid: true };
}

export function getValidationTooltip(params: any, validationContext?: ValidationContext): string {
    const validationResult = validateCellValue(params, validationContext);
    return validationResult.isValid ? '' : validationResult.message || '';
}

export function createValuesGridColumnDefs(validationContext?: ValidationContext): ColDef[] {
    return [
        {
            colId: 'fieldName',
            field: 'fieldName',
            headerName: '',
            minWidth: 150,
            suppressMovable: true,
            lockPosition: 'left',
            lockVisible: true,
            cellStyle: { fontWeight: 'normal' },
            pinned: 'left'
        },
        {
            colId: 'potential',
            field: 'potentialValue',
            headerName: 'Potential',
            headerComponent: CustomHeaderTooltipComponent,
            headerComponentParams: {
                title: 'Potential',
                tooltip: 'A guess at what this Opportunity might be worth. Fill in as much information as is known.'
            },
            editable: (params) => (params.data as ValuesGridRow)?.isEditable ?? false,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: (params: ICellEditorParams) => {
                const data = params.data as ValuesGridRow;
                const fieldType = data?.fieldType;
                if (fieldType === 'percentage') {
                    return {
                        precision: 2,
                        step: 0.01
                    };
                }
                return {};
            },
            valueGetter: (params) => {
                return getPotentialCellValue(params.data as ValuesGridRow);
            },
            valueFormatter: (params: ValueFormatterParams) => {
                return formatPotentialCellDisplay(params);
            },
            valueSetter: (params) => {
                return setPotentialCellValue(params);
            },
            minWidth: 100,
            cellClassRules: {
                'ag-invalid-cell': (params: CellClassParams) => {
                    if (!params.data?.isEditable) return false;
                    const validationParams: CellValidationParams = {
                        data: params.data as ValuesGridRow,
                        value: params.value,
                        node: params.node,
                        colDef: params.colDef,
                        column: params.column,
                        api: params.api
                    };
                    const result = validateCellValue(validationParams, validationContext);
                    return !result.isValid;
                }
            },
            tooltipValueGetter: (params) => {
                if (!params.data?.isEditable) return '';
                const validationParams: CellValidationParams = {
                    data: params.data as ValuesGridRow,
                    value: params.value,
                    node: params.node,
                    colDef: params.colDef,
                    column: params.column,
                    api: params.api
                };
                return getValidationTooltip(validationParams, validationContext);
            },
            lockPinned: true,
            lockVisible: true,
            suppressMovable: true
        },
        {
            colId: 'actual',
            field: 'actualValue',
            headerName: 'Actual',
            headerComponent: CustomHeaderTooltipComponent,
            headerComponentParams: {
                title: 'Actual',
                tooltip:
                    'An accurate value of this Opportunity. All values in this column come from the “Selected” Final Estimate of the linked Bid (if applicable).'
            },
            editable: false,
            cellClassRules: {
                'readonly-cell': () => true
            },
            valueFormatter: (params: ValueFormatterParams) => {
                const data = params.data as ValuesGridRow;
                if (data?.fieldType === 'currency') {
                    return formatCurrencyUSD(params.value);
                } else if (data?.fieldType === 'percentage') {
                    return formatPercentageDisplay(params.value);
                } else if (data?.fieldType === 'number') {
                    return formatNumberDisplay(params.value);
                }
                return '';
            },
            minWidth: 100,
            lockPinned: true,
            lockVisible: true,
            suppressMovable: true
        }
    ];
}

export const VALUES_GRID_COLUMN_DEFS: ColDef[] = createValuesGridColumnDefs();
